import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useFetchSubRModuleData } from '../../../hooks/useFetchSubRModuleData';
import ErrorUI from '../../inventory/ErrorUI';
import LoadingUI from '../../inventory/LoadingUI';
import HeaderSection from './HeaderSection';
import TableViewSection from './TableViewSection';
import { useParams, useSearchParams } from 'react-router-dom';
import { getSubRModuleName } from '../../../helpers/utils';
import { useSelector } from 'react-redux';
import { type RootState } from '../../../../../state/store';
import { convertDateRangeToEpochTimestamp } from '../../../../../helpers/utils';
import { parseSubReportQuery } from '../../../helpers/querySchema';

const TopSalesPerformers = () => {
  const { data, isLoading, error, fetchSubRModuleData } = useFetchSubRModuleData();
  const [searchParams] = useSearchParams();

  const routeMatch = useParams();
  const dashboard = routeMatch?.dashboard ?? '';
  const widget = routeMatch?.widget ?? '';
  const reportType = routeMatch?.reportType ?? '';

  const subRModule = getSubRModuleName(dashboard, widget);
  const {
    primaryValue: value,
    primaryKey: valueKey,
    isValid: isValidQueryParams
  } = parseSubReportQuery({
    searchParams,
    dashboard,
    widget,
    reportType
  });

  const { company_details } = useSelector((state: RootState) => state.company);
  const { dates, timeRange } = useSelector((state: RootState) => state.filters);

  const formattedDates = convertDateRangeToEpochTimestamp(
    dates as [string, string],
    timeRange as [[number, number], [number, number]],
    company_details?.time_zone_offset || '+00:00'
  );
  const startDate = formattedDates[0];
  const endDate = formattedDates[1];

  const encodedValue = value ? encodeURIComponent(value) : '';
  const apiQueryParams = `time_range[start_date]=${startDate}&time_range[end_date]=${endDate}`;
  const apiBaseUrl =
    dashboard && widget && encodedValue && subRModule && isValidQueryParams
      ? `/api/v1/dar/${dashboard}/${widget}/sub-r/${encodedValue}/${subRModule}?${apiQueryParams}`
      : '';

  useEffect(() => {
    if (apiBaseUrl && isValidQueryParams) {
      fetchSubRModuleData(apiBaseUrl);
    }
  }, [apiBaseUrl, isValidQueryParams]);

  const headerTitle = useMemo(() => {
    const rowData = data?.data ?? [];
    if (rowData.length > 0) {
      const staff = rowData[0]?.staff;
      return staff ? `Orders from ${staff}` : 'Orders';
    }
    return 'Orders';
  }, [data]);

  const handleRetry = () => {
    fetchSubRModuleData(apiBaseUrl);
  };

  if (isLoading) {
    return <LoadingUI />;
  }

  if (error) {
    return <ErrorUI onRetry={handleRetry} />;
  }

  if (!data) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderSection headerTitle={headerTitle} exportData={data} />
      <TableViewSection contentData={data} value={value} valueKey={valueKey} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

export default TopSalesPerformers;
